import React from 'react';
import { Box, Container, Typography, Paper, Divider, List, ListItem, ListItemText, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';

const TermsOfServicePage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default, py: 8 }}>
      <Container maxWidth="md">
        <IconButton onClick={() => navigate('/')} sx={{ mb: 3 }}>
          <ArrowBackIcon /> <Typography sx={{ ml: 1 }}>{t('back_to_home')}</Typography>
        </IconButton>

        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, borderRadius: 3 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            {t('terms_of_service_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            {t('last_updated')}: {new Date().toLocaleDateString()}
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* Agreement to Terms */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('agreement_to_terms_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('agreement_to_terms_text')}
            </Typography>
          </Box>

          {/* Service Description */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('service_description_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('service_description_text')}
            </Typography>
          </Box>

          {/* User Accounts */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('user_accounts_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('user_accounts_text')}
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary={t('account_responsibility_1')} />
              </ListItem>
              <ListItem>
                <ListItemText primary={t('account_responsibility_2')} />
              </ListItem>
              <ListItem>
                <ListItemText primary={t('account_responsibility_3')} />
              </ListItem>
              <ListItem>
                <ListItemText primary={t('account_responsibility_4')} />
              </ListItem>
            </List>
          </Box>

          {/* Subscription and Payment */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('subscription_payment_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('subscription_payment_text')}
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary={t('billing_title')}
                  secondary={t('billing_text')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('auto_renewal_title')}
                  secondary={t('auto_renewal_text')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('cancellation_title')}
                  secondary={t('cancellation_text')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('refund_policy_title')}
                  secondary={t('refund_policy_text')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            </List>
          </Box>

          {/* Acceptable Use */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('acceptable_use_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('acceptable_use_text')}
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary={t('prohibited_use_1')} />
              </ListItem>
              <ListItem>
                <ListItemText primary={t('prohibited_use_2')} />
              </ListItem>
              <ListItem>
                <ListItemText primary={t('prohibited_use_3')} />
              </ListItem>
              <ListItem>
                <ListItemText primary={t('prohibited_use_4')} />
              </ListItem>
              <ListItem>
                <ListItemText primary={t('prohibited_use_5')} />
              </ListItem>
            </List>
          </Box>

          {/* Intellectual Property */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('intellectual_property_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('intellectual_property_text')}
            </Typography>
          </Box>

          {/* Data Protection */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('data_protection_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('data_protection_text')}
            </Typography>
          </Box>

          {/* Service Availability */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('service_availability_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('service_availability_text')}
            </Typography>
          </Box>

          {/* Limitation of Liability */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('limitation_liability_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('limitation_liability_text')}
            </Typography>
          </Box>

          {/* Indemnification */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('indemnification_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('indemnification_text')}
            </Typography>
          </Box>

          {/* Governing Law */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('governing_law_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('governing_law_text')}
            </Typography>
          </Box>

          {/* Changes to Terms */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('changes_to_terms_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('changes_to_terms_text')}
            </Typography>
          </Box>

          {/* Contact Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('contact_information_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('contact_information_text')}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfServicePage;
